/**
 * Pure derivation of a gag's £ budget-status glyph — the sibling of the
 * three-state gag RA badge (GAG_RA_BADGES). Extracted so the breakdown table
 * row and the detail panel header share ONE source of truth for the state +
 * tooltip (mirrors utils/budgetPushState.js's role for the push button).
 *
 * Inputs (straight off the gag payload):
 *  - estimatedCost: gags.estimated_cost (number | null)
 *  - budgeted:      read-only server flag — an ACTIVE linked budget line
 *                   exists in the department's ACTIVE budget version (bool)
 *
 * Returns { state, tooltip } where state ∈ 'none' | 'estimated' | 'budgeted'
 * (index into GAG_BUDGET_BADGES for the colour class) and tooltip is the human
 * string for the native title attribute.
 *
 *  - budgeted true            → 'budgeted' (green) — wins even with an estimate
 *                               passed alongside (a pushed gag always has one).
 *  - estimate set, not pushed → 'estimated' (amber).
 *  - no estimate (null)       → 'none' (faded grey).
 */
import { formatCurrency } from './formatCurrency';
import { roundMoney } from './money';

export function gagBudgetStatus({ estimatedCost, budgeted }) {
  // A pushed gag is green regardless of the estimate echoed alongside it.
  if (budgeted) {
    // MEMBER-BREADTH THINNING (2026-08-15): the server strips estimated_cost
    // from every gag payload for callers lacking canViewBudget/canManageBreakdown
    // in the department, so a BUDGETED gag can arrive with no cost. The amount
    // is then OMITTED from the tooltip — `formatCurrency(undefined)` coerces
    // to "£0.00", and a fabricated zero is worse than an absence (the CLAUDE.md
    // "no NaN/£0 fabrication" rule). The 'estimated' state is unreachable with
    // a stripped cost (its guard is `estimatedCost != null`), so this branch is
    // the only one that needed the guard.
    return {
      state: 'budgeted',
      tooltip: estimatedCost != null
        ? `${formatCurrency(estimatedCost)} estimated — added to the budget`
        : 'Added to the budget',
    };
  }
  if (estimatedCost != null) {
    return {
      state: 'estimated',
      tooltip: `${formatCurrency(estimatedCost)} estimated — not yet added to the budget`,
    };
  }
  return { state: 'none', tooltip: 'No cost estimate yet.' };
}

/**
 * 📱 THE PHONE CARD's budget item (owner 2026-10-02: a bare *"Budget £"* read as
 * broken). Words, not a coloured glyph, because a phone has no hover and the
 * `£`'s colour was the only thing saying which state it was in. Built on
 * `gagBudgetStatus` so the card and the table's `£` glyph can never disagree
 * about the STATE; only the presentation differs.
 *
 * Returns `{ state, label, amount }` — `amount` is the formatted figure or
 * `null`, `label` the words beside/instead of it:
 *  - budgeted, cost visible  → `Budget` + `£1,200.00`
 *  - budgeted, cost stripped → `In budget`, no figure (the viewer may not see
 *    costs — `GAG_COST_FIELDS` thinning — and a fabricated £0.00 is worse than
 *    an absence)
 *  - estimate, not budgeted  → `Est.` + `£1,200.00`: an estimate is NOT a
 *    budget yet, and calling it "Budget" would say it was
 *  - neither                 → `No budget` (true for every viewer: `budgeted`
 *    is never stripped, and with no line there is no budget)
 * 🔑 Absence is `== null`, never falsy — a real `0` estimate is a figure.
 */
export function gagCardBudget({ estimatedCost, budgeted }) {
  const { state } = gagBudgetStatus({ estimatedCost, budgeted });
  const amount = estimatedCost != null ? formatCurrency(estimatedCost) : null;
  if (state === 'budgeted') return { state, label: amount ? 'Budget' : 'In budget', amount };
  if (state === 'estimated') return { state, label: 'Est.', amount };
  return { state, label: 'No budget', amount: null };
}

/**
 * THE BUDGET PROPOSAL (owner ruling 2026-09-30): the gag's JOB CODE budget (an
 * ad-hoc line until the job cost codes of 2026-10-28) is the SOURCE OF TRUTH for a budgeted gag's cost, and a saved estimate that differs
 * from it is a PROPOSAL awaiting someone with budget rights.
 *
 * Inputs straight off the DETAIL payload: `budgeted`, `estimatedCost`
 * (gags.estimated_cost) and `budgetLineAmount` (the active-version line's own
 * amount). Returns { pending, lineAmount, tooltip }.
 *
 * 🔒 A caller who may not see costs gets NEITHER figure (the server strips
 * GAG_COST_FIELDS), so `budgetLineAmount` is absent and this answers
 * `pending: false, lineAmount: null` — no tag, no "Allocated budget", no
 * figure. That is the whole thinning contract on this surface: never derive a
 * pending state from `budgeted` alone.
 * A cleared estimate on a budgeted gag IS pending (it differs from the line)
 * — unless the line is £0, which is the same absence.
 */
export function gagBudgetProposal({ budgeted, estimatedCost, budgetLineAmount }) {
  const lineAmount = budgetLineAmount == null ? null : roundMoney(budgetLineAmount);
  if (budgeted !== true || lineAmount === null) {
    return { pending: false, lineAmount: null, tooltip: null };
  }
  // 🔑 £0 IS "NO ESTIMATE" ON BOTH SIDES (house rule, the server's
  // normalizeMoney: 0 = cleared). A £0 line writes the gag estimate back as
  // NULL, so a NULL estimate against a £0 line is IN STEP — reading it as
  // pending would offer "Keep budget amount", which writes NULL again: a cycle.
  const rawEstimate = estimatedCost == null || estimatedCost === '' ? null : roundMoney(estimatedCost);
  const estimate = rawEstimate === 0 ? null : rawEstimate;
  const target = lineAmount === 0 ? null : lineAmount;
  const pending = estimate !== target;
  return {
    pending,
    lineAmount,
    tooltip: pending
      ? `This estimate differs from its job code budget (${formatCurrency(lineAmount)}). Someone with budget rights needs to update the job code budget.`
      : null,
  };
}
